import React, { useState, useEffect, useRef, useCallback } from 'react';
import { scanApi, stockApi, productsApi, depotsApi, labelsApi, preparationsApi } from '../lib/api';
import api, { fetchBlob } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import {
  Camera, Package,
  ArrowDownRight, ArrowUpRight, ArrowLeftRight, Search,
  CheckCircle, Scan, ImageIcon,
  ClipboardCheck, CameraOff, Tag, Printer, Box, Minus, Plus,
  Truck, XCircle, AlertTriangle, ListChecks, X
} from 'lucide-react';

const MobileScanPage = () => {
  const [mode, setMode] = useState('scan');
  const [cameraActive, setCameraActive] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [depots, setDepots] = useState([]);

  // Movement state
  const [movementType, setMovementType] = useState('entry');
  const [movementQty, setMovementQty] = useState('1');
  const [movementDepot, setMovementDepot] = useState('');

  // Labels state
  const [labelQty, setLabelQty] = useState('1');
  const [labelUnitType, setLabelUnitType] = useState('carton');
  const [labelQtyPerUnit, setLabelQtyPerUnit] = useState('');
  const [labelList, setLabelList] = useState([]);

  // Photo state
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [photoTarget, setPhotoTarget] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const cameraPhotoRef = useRef(null);

  // Preparation validation state
  const [preparations, setPreparations] = useState([]);
  const [selectedPrepId, setSelectedPrepId] = useState('');
  const [selectedPrep, setSelectedPrep] = useState(null);
  const [prepItems, setPrepItems] = useState([]); // enriched items with product info
  const [scannedMap, setScannedMap] = useState({}); // {product_id: scanned_qty}
  const [prepValidating, setPrepValidating] = useState(false);

  // Camera
  const scannerRef = useRef(null);

  useEffect(() => {
    depotsApi.getAll().then(r => setDepots(r.data || [])).catch(() => {});
    // Load pending preparations for validation mode
    preparationsApi.getAll({ status: 'pending' }).then(r => {
      setPreparations(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
  }, []);

  // Load preparation details when selected
  useEffect(() => {
    if (!selectedPrepId) { setSelectedPrep(null); setPrepItems([]); setScannedMap({}); return; }
    const load = async () => {
      try {
        const res = await preparationsApi.getById(selectedPrepId);
        const prep = res.data;
        setSelectedPrep(prep);
        setScannedMap({});
        // Enrich items with product info
        const enriched = await Promise.all((prep.items || []).map(async (item) => {
          try {
            const pRes = await productsApi.getById(item.product_id);
            const p = pRes.data?.product || pRes.data || {};
            return { ...item, reference: p.reference, designation: p.designation, category: p.category, risk_class: p.risk_class };
          } catch { return { ...item, reference: item.product_id, designation: '?' }; }
        }));
        setPrepItems(enriched);
      } catch { toast.error('Erreur chargement preparation'); }
    };
    load();
  }, [selectedPrepId]);

  // QR Camera Scanner
  const startCamera = useCallback(async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      
      const config = {
        fps: 10,
        qrbox: { width: 200, height: 200 },
        aspectRatio: 1.0,
        disableFlip: false,
      };
      
      await scanner.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          handleScan(decodedText);
          scanner.pause(true);
          setTimeout(() => { try { scanner.resume(); } catch {} }, 2000);
        },
        () => {}
      );
      setCameraActive(true);
    } catch (err) {
      console.error("Camera error:", err);
      toast.error("Impossible d'acceder a la camera. Verifiez les permissions.");
      setCameraActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (scannerRef.current) {
      try { scannerRef.current.stop().catch(() => {}); } catch {}
      scannerRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const handleScan = async (data) => {
    if (!data || loading) return;
    setLoading(true);
    setScanInput(data);
    try {
      let parsed;
      try {
        parsed = JSON.parse(data);
        if (typeof parsed !== 'object' || parsed === null) parsed = { id: data };
      } catch { parsed = { id: data }; }
      const res = await scanApi.process(parsed);
      setScanResult(res.data);
      toast.success(`${res.data.type === 'product' ? 'Produit' : 'Carton'} trouve`);
      if (res.data.product?.id) loadPhotos(res.data.product.id);
      // Pre-fill label quantity from product packaging
      if (res.data.product?.pieces_per_box || res.data.product?.packaging) {
        setLabelQtyPerUnit(String(res.data.product.pieces_per_box || res.data.product.packaging || ''));
      }
      // Auto-validate in preparation mode
      if (mode === 'preparation' && res.data.product) {
        handlePrepScan(res.data.product);
      }
    } catch {
      toast.error('Element non trouve');
      setScanResult(null);
    }
    setLoading(false);
  };

  const handleManualScan = () => { if (scanInput.trim()) handleScan(scanInput.trim()); };

  // Handle scan in preparation validation mode
  const handlePrepScan = (product) => {
    if (!product || !selectedPrep) return;
    const pid = product.id;
    const expected = prepItems.find(i => i.product_id === pid);
    if (!expected) {
      toast.error(`${product.reference} n'est pas dans cette preparation`);
      return;
    }
    const currentScanned = scannedMap[pid] || 0;
    if (currentScanned >= expected.quantity) {
      toast.info(`${product.reference} deja entierement scanne`);
      return;
    }
    setScannedMap(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
    toast.success(`${product.reference} +1 (${currentScanned + 1}/${expected.quantity})`);
  };

  const prepTotalExpected = prepItems.reduce((s, i) => s + (i.quantity || 0), 0);
  const prepTotalScanned = Object.values(scannedMap).reduce((s, v) => s + v, 0);
  const prepAllScanned = prepItems.length > 0 && prepItems.every(i => (scannedMap[i.product_id] || 0) >= i.quantity);

  const handleValidatePrep = async () => {
    if (!selectedPrep || !prepAllScanned) return;
    setPrepValidating(true);
    try {
      const items = prepItems.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
      }));
      await preparationsApi.validate(selectedPrepId, items);
      toast.success('Preparation validee et expediee');
      setSelectedPrepId('');
      setSelectedPrep(null);
      setPrepItems([]);
      setScannedMap({});
      // Refresh list
      preparationsApi.getAll({ status: 'pending' }).then(r => setPreparations(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } catch (err) { toast.error(getApiErrorMessage(err, 'Erreur validation')); }
    setPrepValidating(false);
  };

  const loadPhotos = async (productId) => {
    try { const res = await api.get(`/products/${productId}/photos`); setPhotos(res.data || []); }
    catch { setPhotos([]); }
  };

  const handlePhotoUpload = async (file) => {
    if (!photoTarget || !file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/products/${photoTarget}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo enregistree');
      loadPhotos(photoTarget);
      setShowPhotoDialog(false);
    } catch (err) { toast.error(getApiErrorMessage(err, 'Erreur upload photo')); }
    setUploading(false);
  };

  const handleDeletePhoto = async (filename) => {
    if (!product?.id || !filename) return;
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      await api.delete(`/products/${product.id}/photo/${filename}`);
      toast.success('Photo supprimée');
      loadPhotos(product.id);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur suppression photo'));
    }
  };

  const handleStockMovement = async () => {
    if (!scanResult?.product?.id || !movementDepot || !movementQty) {
      toast.error('Remplissez tous les champs'); return;
    }
    setLoading(true);
    try {
      await stockApi.createMovement({
        product_id: scanResult.product.id, depot_id: movementDepot,
        movement_type: movementType, quantity: parseInt(movementQty) || 1,
        reason: `Scan mobile - ${movementType}`
      });
      toast.success(`Mouvement ${movementType} enregistre`);
      setMovementQty('1');
    } catch (err) { toast.error(getApiErrorMessage(err, 'Erreur mouvement')); }
    setLoading(false);
  };

  // Add product to label print list
  const addToLabelList = () => {
    if (!scanResult?.product) { toast.error('Scannez un produit d\'abord'); return; }
    const qty = parseInt(labelQty) || 1;
    const qpu = labelQtyPerUnit ? parseInt(labelQtyPerUnit) : null;
    const p = scanResult.product;
    setLabelList(prev => {
      const existing = prev.find(l => l.product_id === p.id && l.unit_type === labelUnitType && l.quantity_per_unit === qpu);
      if (existing) {
        return prev.map(l => l === existing ? { ...l, quantity: l.quantity + qty } : l);
      }
      return [...prev, {
        product_id: p.id, reference: p.reference, designation: p.designation,
        quantity: qty, unit_type: labelUnitType,
        quantity_per_unit: qpu,
      }];
    });
    toast.success(`${qty} etiquette(s) ajoutee(s) - ${labelUnitType === 'carton' ? 'Carton' : 'Piece'}`);
    setLabelQty('1');
  };

  const removeFromLabelList = (idx) => setLabelList(prev => prev.filter((_, i) => i !== idx));

  const printLabels = async () => {
    if (labelList.length === 0) { toast.error('Liste vide'); return; }
    setLoading(true);
    try {
      const res = await fetchBlob('POST', '/labels/generate', {
        items: labelList.map(l => ({
          product_id: l.product_id,
          quantity: l.quantity,
          unit_type: l.unit_type,
          quantity_per_unit: l.quantity_per_unit,
        }))
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      toast.success('PDF genere');
    } catch (err) { toast.error('Erreur generation PDF'); }
    setLoading(false);
  };

  const totalLabels = labelList.reduce((s, l) => s + l.quantity, 0);

  const ProductCard = ({ product, box }) => (
    <Card className="bg-zinc-900/80 border-zinc-800" data-testid="scan-result-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-cyan-400 text-sm">{product?.reference}</p>
            <p className="text-white font-semibold text-base mt-1">{product?.designation}</p>
          </div>
          <div className="flex gap-1 ml-2 flex-shrink-0">
            {product?.category && <Badge variant="outline" className="text-xs">{product.category}</Badge>}
            {product?.risk_class && <Badge variant="outline" className="text-xs text-amber-400 border-amber-500/30">{product.risk_class}</Badge>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {product?.homologation_number && (
            <div className="bg-zinc-950 p-2 rounded">
              <p className="text-zinc-500">Agrement</p>
              <p className="text-green-400 font-mono text-[11px]">{product.homologation_number}</p>
            </div>
          )}
          {product?.nec != null && (
            <div className="bg-zinc-950 p-2 rounded">
              <p className="text-zinc-500">NEC</p>
              <p className="text-amber-400 font-mono">{product.nec ? `${Math.round(product.nec * 1000 * 100) / 100} g` : '-'}</p>
            </div>
          )}
          {product?.un_code && (
            <div className="bg-zinc-950 p-2 rounded">
              <p className="text-zinc-500">Code ONU</p>
              <p className="text-white font-mono">{product.un_code}</p>
            </div>
          )}
        </div>
        {box && (
          <div className="bg-zinc-950 p-2 rounded text-xs">
            <p className="text-zinc-500">Carton: <span className="text-cyan-400 font-mono">{box.id}</span> | Qte: <span className="text-white">{box.quantity}</span></p>
          </div>
        )}
        {photos.length > 0 && (
          <div>
            <p className="text-xs text-zinc-500 mb-1">Photos ({photos.length})</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {photos.map((ph, i) => (
                <div key={i} className="relative flex-shrink-0 group">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} alt=""
                    className="w-16 h-16 object-cover rounded border border-zinc-700" />
                  <button type="button" onClick={() => handleDeletePhoto(ph.filename)}
                    data-testid={`delete-photo-${i}`}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center shadow-md border border-zinc-900 transition-colors"
                    aria-label="Supprimer la photo">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 flex-1"
            onClick={() => { setPhotoTarget(product?.id); setShowPhotoDialog(true); }} data-testid="take-photo-btn">
            <Camera className="w-4 h-4 mr-1" /> Photo
          </Button>
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 flex-1"
            onClick={() => setMode('movement')} data-testid="stock-movement-btn">
            <ArrowLeftRight className="w-4 h-4 mr-1" /> Mouvement
          </Button>
          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 flex-1"
            onClick={() => setMode('labels')} data-testid="label-mode-btn">
            <Tag className="w-4 h-4 mr-1" /> Etiquette
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="mobile-scan-page">
      {/* Mode selector */}
      <div className="flex gap-1 bg-zinc-900/50 p-1 rounded-lg border border-zinc-800">
        {[
          { key: 'scan', label: 'Scanner', icon: Scan },
          { key: 'preparation', label: 'Prepa', icon: Truck },
          { key: 'labels', label: 'Etiquettes', icon: Tag },
          { key: 'movement', label: 'Mvt', icon: ArrowLeftRight },
          { key: 'inventory', label: 'Inventaire', icon: ClipboardCheck },
        ].map(({ key, label, icon: Icon }) => (
          <Button key={key} variant={mode === key ? 'default' : 'ghost'} size="sm"
            className={`flex-1 text-xs px-1 ${mode === key ? 'bg-amber-500 text-black' : 'text-zinc-400'}`}
            onClick={() => setMode(key)} data-testid={`mode-${key}`}>
            <Icon className="w-3.5 h-3.5 mr-0.5" /> {label}
          </Button>
        ))}
      </div>

      {/* Camera + manual input */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button size="sm" onClick={cameraActive ? stopCamera : startCamera}
              className={cameraActive ? 'bg-red-500 hover:bg-red-400 text-white' : 'bg-cyan-500 hover:bg-cyan-400 text-black'}
              data-testid="toggle-camera-btn">
              {cameraActive ? <CameraOff className="w-4 h-4 mr-1" /> : <Camera className="w-4 h-4 mr-1" />}
              {cameraActive ? 'Stop' : 'Camera'}
            </Button>
            <div className="flex-1 flex gap-1">
              <Input value={scanInput} onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleManualScan()}
                placeholder="ID ou reference..."
                className="bg-zinc-950 border-zinc-700 text-white text-sm h-9 flex-1"
                data-testid="scan-input" />
              <Button size="sm" onClick={handleManualScan} disabled={loading}
                className="bg-amber-500 hover:bg-amber-400 text-black h-9 px-3" data-testid="scan-submit">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Camera preview */}
          <div 
            id="qr-reader" 
            className={`rounded-lg overflow-hidden bg-black ${cameraActive ? 'block' : 'hidden'}`}
            style={{ width: '100%', minHeight: cameraActive ? '300px' : '0' }}
            data-testid="qr-reader"
          />
          
          {!cameraActive && !scanResult && (
            <div className="text-center py-6 border-2 border-dashed border-zinc-800 rounded-lg">
              <Camera className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Appuyez sur Camera pour scanner</p>
              <p className="text-zinc-600 text-xs mt-1">ou saisissez une reference manuellement</p>
            </div>
          )}
          
          {loading && (
            <div className="flex items-center justify-center py-3">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scan Result */}
      {scanResult && <ProductCard product={scanResult.product} box={scanResult.box} />}

      {/* ========== PREPARATION VALIDATION MODE ========== */}
      {mode === 'preparation' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="preparation-panel">
          <CardContent className="p-4 space-y-3">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <Truck className="w-4 h-4 text-amber-400" /> Validation de preparation
            </p>

            {/* Preparation selector */}
            <Select value={selectedPrepId} onValueChange={setSelectedPrepId}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white text-sm" data-testid="prep-select">
                <SelectValue placeholder="Choisir une preparation..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {preparations.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-white">
                    {p.id.slice(-6)} — {p.client || 'Sans client'} ({(p.items || []).length} art.)
                  </SelectItem>
                ))}
                {preparations.length === 0 && (
                  <SelectItem value="_none" disabled className="text-zinc-500">Aucune preparation en attente</SelectItem>
                )}
              </SelectContent>
            </Select>

            {/* Progress & item list */}
            {selectedPrep && prepItems.length > 0 && (
              <>
                {/* Progress bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Progression</span>
                    <span className={`font-mono font-bold ${prepAllScanned ? 'text-green-400' : 'text-amber-400'}`}>
                      {prepTotalScanned} / {prepTotalExpected}
                    </span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${prepAllScanned ? 'bg-green-500' : 'bg-amber-500'}`}
                      style={{ width: `${prepTotalExpected > 0 ? Math.min(100, (prepTotalScanned / prepTotalExpected) * 100) : 0}%` }} />
                  </div>
                </div>

                {/* Items list */}
                <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                  {prepItems.map((item) => {
                    const scanned = scannedMap[item.product_id] || 0;
                    const done = scanned >= item.quantity;
                    const over = scanned > item.quantity;
                    return (
                      <div key={item.product_id}
                        className={`flex items-center gap-2 p-2 rounded-lg border transition-colors ${
                          done ? 'bg-green-500/10 border-green-500/30' : 'bg-zinc-950 border-zinc-800'
                        }`} data-testid={`prep-item-${item.product_id}`}>
                        {done ? <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" /> :
                          <XCircle className="w-4 h-4 text-zinc-600 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-cyan-400 text-[11px]">{item.reference}</p>
                          <p className="text-white text-xs truncate">{item.designation}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-400"
                            onClick={() => setScannedMap(prev => ({ ...prev, [item.product_id]: Math.max(0, (prev[item.product_id] || 0) - 1) }))}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className={`font-mono text-sm font-bold min-w-[3ch] text-center ${
                            done ? 'text-green-400' : over ? 'text-red-400' : 'text-white'
                          }`}>{scanned}</span>
                          <span className="text-zinc-600 text-xs">/ {item.quantity}</span>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-400"
                            onClick={() => setScannedMap(prev => ({ ...prev, [item.product_id]: (prev[item.product_id] || 0) + 1 }))}>
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Validate button */}
                {prepAllScanned ? (
                  <Button className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold"
                    onClick={handleValidatePrep} disabled={prepValidating} data-testid="validate-prep-btn">
                    {prepValidating ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Valider et expedier
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <p className="text-amber-400 text-xs">
                      Scannez les produits manquants. {prepTotalExpected - prepTotalScanned} unite(s) restante(s).
                    </p>
                  </div>
                )}
              </>
            )}

            {selectedPrep && prepItems.length === 0 && (
              <p className="text-zinc-500 text-xs text-center py-4">Aucun article dans cette preparation</p>
            )}

            {!selectedPrepId && (
              <p className="text-zinc-500 text-xs text-center py-4">
                Selectionnez une preparation puis scannez chaque produit pour valider le picking.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ========== LABELS MODE ========== */}
      {mode === 'labels' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="labels-panel">
          <CardContent className="p-4 space-y-3">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <Tag className="w-4 h-4 text-amber-400" /> Impression d'etiquettes
            </p>

            {scanResult?.product ? (
              <>
                <div className="bg-zinc-950 rounded-lg p-3 border border-zinc-800">
                  <p className="font-mono text-cyan-400 text-xs">{scanResult.product.reference}</p>
                  <p className="text-white text-sm">{scanResult.product.designation}</p>
                </div>

                {/* Unit type selector */}
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm"
                    variant={labelUnitType === 'carton' ? 'default' : 'outline'}
                    className={labelUnitType === 'carton'
                      ? 'bg-amber-500 text-black hover:bg-amber-400'
                      : 'border-zinc-700 text-zinc-400'}
                    onClick={() => setLabelUnitType('carton')}
                    data-testid="unit-carton">
                    <Box className="w-4 h-4 mr-2" /> Carton
                  </Button>
                  <Button size="sm"
                    variant={labelUnitType === 'piece' ? 'default' : 'outline'}
                    className={labelUnitType === 'piece'
                      ? 'bg-cyan-500 text-black hover:bg-cyan-400'
                      : 'border-zinc-700 text-zinc-400'}
                    onClick={() => setLabelUnitType('piece')}
                    data-testid="unit-piece">
                    <Package className="w-4 h-4 mr-2" /> Piece unitaire
                  </Button>
                </div>

                {/* Quantity per unit */}
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">
                    {labelUnitType === 'carton' ? 'Nombre de pieces par carton' : 'Quantite par etiquette'}
                  </p>
                  <Input type="number" min="1" value={labelQtyPerUnit}
                    onChange={e => setLabelQtyPerUnit(e.target.value)}
                    placeholder={labelUnitType === 'carton' ? 'pcs/carton' : 'quantite'}
                    className="bg-zinc-950 border-zinc-700 text-white text-center font-mono"
                    data-testid="label-qty-per-unit" />
                </div>

                {/* Number of labels */}
                <div className="space-y-1">
                  <p className="text-zinc-500 text-xs">Nombre d'etiquettes a imprimer</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 h-9 w-9 p-0"
                      onClick={() => setLabelQty(q => String(Math.max(1, (parseInt(q) || 1) - 1)))}>
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input type="number" min="1" value={labelQty}
                      onChange={e => setLabelQty(e.target.value)}
                      className="bg-zinc-950 border-zinc-700 text-white text-center font-mono text-lg flex-1 h-9"
                      data-testid="label-qty" />
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 h-9 w-9 p-0"
                      onClick={() => setLabelQty(q => String((parseInt(q) || 0) + 1))}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <Button className="w-full bg-green-600 hover:bg-green-500 text-white"
                  onClick={addToLabelList} data-testid="add-to-label-list">
                  <Plus className="w-4 h-4 mr-2" /> Ajouter a la liste
                </Button>
              </>
            ) : (
              <p className="text-zinc-500 text-xs text-center py-4">
                Scannez un produit pour creer des etiquettes
              </p>
            )}

            {/* Label list */}
            {labelList.length > 0 && (
              <div className="space-y-2 border-t border-zinc-800 pt-3">
                <div className="flex items-center justify-between">
                  <p className="text-zinc-400 text-xs font-medium">
                    Liste ({labelList.length} produit{labelList.length > 1 ? 's' : ''}, {totalLabels} etiquette{totalLabels > 1 ? 's' : ''})
                  </p>
                  <Button size="sm" variant="ghost" className="text-red-400 text-xs h-6 px-2"
                    onClick={() => setLabelList([])}>Vider</Button>
                </div>
                {labelList.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-zinc-950 p-2 rounded-lg border border-zinc-800">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-cyan-400 text-[11px]">{item.reference}</p>
                      <p className="text-white text-xs truncate">{item.designation}</p>
                      <div className="flex gap-2 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] ${item.unit_type === 'carton' ? 'text-amber-400 border-amber-500/30' : 'text-cyan-400 border-cyan-500/30'}`}>
                          {item.unit_type === 'carton' ? 'Carton' : 'Piece'}
                        </Badge>
                        {item.quantity_per_unit && (
                          <span className="text-zinc-500 text-[10px]">
                            {item.quantity_per_unit} {item.unit_type === 'carton' ? 'pcs/crt' : 'pce(s)'}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-white font-mono text-lg font-bold">{item.quantity}</span>
                    <Button size="sm" variant="ghost" className="text-red-400 h-7 w-7 p-0"
                      onClick={() => removeFromLabelList(idx)} data-testid={`remove-label-${idx}`}>
                      <Minus className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                  onClick={printLabels} disabled={loading} data-testid="print-labels-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer {totalLabels} etiquette{totalLabels > 1 ? 's' : ''}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ========== MOVEMENT MODE ========== */}
      {mode === 'movement' && scanResult?.product && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="movement-panel">
          <CardContent className="p-4 space-y-3">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <ArrowLeftRight className="w-4 h-4 text-amber-400" /> Mouvement — {scanResult.product.reference}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { val: 'entry', label: 'Entree', icon: ArrowDownRight, color: 'text-green-400' },
                { val: 'exit', label: 'Sortie', icon: ArrowUpRight, color: 'text-red-400' },
                { val: 'transfer', label: 'Transfert', icon: ArrowLeftRight, color: 'text-blue-400' },
              ].map(({ val, label, icon: Icon, color }) => (
                <Button key={val} size="sm" variant={movementType === val ? 'default' : 'outline'}
                  className={`text-xs ${movementType === val ? 'bg-amber-500 text-black' : `border-zinc-700 ${color}`}`}
                  onClick={() => setMovementType(val)}>
                  <Icon className="w-3.5 h-3.5 mr-1" /> {label}
                </Button>
              ))}
            </div>
            <Select value={movementDepot} onValueChange={setMovementDepot}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white text-sm" data-testid="movement-depot">
                <SelectValue placeholder="Depot..." />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {depots.map(d => <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input type="number" min="1" value={movementQty} onChange={e => setMovementQty(e.target.value)}
                className="bg-zinc-950 border-zinc-700 text-white text-center font-mono w-24" data-testid="movement-qty" />
              <Button className="flex-1 bg-green-600 hover:bg-green-500 text-white" onClick={handleStockMovement}
                disabled={loading} data-testid="confirm-movement-btn">
                <CheckCircle className="w-4 h-4 mr-2" /> Valider
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========== INVENTORY MODE ========== */}
      {mode === 'inventory' && (
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="inventory-panel">
          <CardContent className="p-4 space-y-3">
            <p className="text-white font-semibold text-sm flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-amber-400" /> Inventaire rapide
            </p>
            <p className="text-zinc-500 text-xs">Scannez les produits un par un.</p>
            {scanResult?.product && (
              <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-cyan-400 text-xs">{scanResult.product.reference}</p>
                    <p className="text-white text-sm">{scanResult.product.designation}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photo Upload Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="photo-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Camera className="w-5 h-5 text-cyan-400" /> Prendre une photo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-zinc-500 text-xs">Photographiez l'etiquette ou le produit.</p>
            <input type="file" ref={photoInputRef} accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
            <input type="file" ref={cameraPhotoRef} accept="image/*" className="hidden"
              onChange={(e) => handlePhotoUpload(e.target.files?.[0])} />
            <div className="grid grid-cols-2 gap-2">
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black h-20 flex-col gap-2"
                onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="camera-capture-btn">
                <Camera className="w-8 h-8" /><span className="text-xs">Appareil photo</span>
              </Button>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 h-20 flex-col gap-2"
                onClick={() => cameraPhotoRef.current?.click()} disabled={uploading} data-testid="gallery-upload-btn">
                <ImageIcon className="w-8 h-8" /><span className="text-xs">Galerie</span>
              </Button>
            </div>
            {uploading && (
              <div className="flex items-center justify-center py-2">
                <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mr-2" />
                <span className="text-zinc-400 text-sm">Envoi en cours...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileScanPage;
